/**
 * CaseDescriptionSection.jsx
 * 
 * Structured case description display with clear hierarchy:
 * 1. Primary Case Overview - Clean, concise summary from structured fields
 * 2. Official Circumstances - Short structured paragraph from official record
 * 3. Expanded Narrative - Source-specific narratives (e.g., Charley Project)
 * 4. Source Transparency - List of data sources with links
 */

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { 
  FileText, User, MapPin, Calendar, ExternalLink, 
  ChevronDown, ChevronUp, Database, BookOpen, Shield
} from 'lucide-react';

// Source display configuration
const SOURCE_CONFIG = {
  namus: { 
    name: 'NamUs Missing', 
    fullName: 'National Missing and Unidentified Persons System - Missing Persons',
    color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    urlPrefix: 'https://namus.nij.ojp.gov/case/'
  },
  namus_unidentified: { 
    name: 'NamUs Unidentified', 
    fullName: 'National Missing and Unidentified Persons System - Unidentified Persons',
    color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400',
    urlPrefix: 'https://namus.nij.ojp.gov/case/'
  },
  namus_unclaimed: { 
    name: 'NamUs Unclaimed', 
    fullName: 'National Missing and Unidentified Persons System - Unclaimed Persons',
    color: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400',
    urlPrefix: 'https://namus.nij.ojp.gov/case/'
  },
  ncmec: { 
    name: 'NCMEC', 
    fullName: 'National Center for Missing & Exploited Children',
    color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
    urlPrefix: 'https://www.missingkids.org/poster/'
  },
  charley: { 
    name: 'The Charley Project', 
    fullName: 'The Charley Project',
    color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
    urlPrefix: 'https://charleyproject.org/case/'
  },
  doenetwork: { 
    name: 'Doe Network', 
    fullName: 'Doe Network: International Center for Unidentified & Missing Persons',
    color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
    urlPrefix: 'https://www.doenetwork.org/'
  },
  manual: {
    name: 'Manual Entry',
    fullName: 'Manually Added Case',
    color: 'bg-slate-100 text-slate-800 dark:bg-slate-900/30 dark:text-slate-400',
    urlPrefix: null
  },
  canonical: {
    name: 'Merged Record',
    fullName: 'Merged from Multiple Sources',
    color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
    urlPrefix: null
  }
};

import { formatDate } from '@/utils/dateUtils';

// Format date for display - uses centralized utility
const formatDisplayDate = (dateStr) => {
  return formatDate(dateStr, { fallback: null });
};

// Build primary overview text from structured fields
const buildPrimaryOverview = (caseData) => {
  const parts = [];
  const name = caseData.name?.replace(/^:\s*/, '').trim() || 'This individual';
  
  // Determine case type for context - prioritize explicit case_type over source inference
  const isUnidentified = caseData.case_type === 'unidentified' || 
                         caseData.case_type === 'unidentified_person' ||
                         caseData.source === 'namus_unidentified' ||
                         // Only treat doenetwork as unidentified if case_type is explicitly unidentified
                         // or if name suggests it's unidentified (starts with "Unidentified" or has Doe designation)
                         (caseData.source === 'doenetwork' && 
                          (caseData.case_type === 'unidentified_person' || 
                           caseData.name?.toLowerCase().includes('unidentified') ||
                           caseData.name?.toLowerCase().includes(' doe')));
  const isUnclaimed = caseData.case_type === 'unclaimed_person' || 
                      caseData.case_type === 'unclaimed' ||
                      caseData.source === 'namus_unclaimed';
  
  // Helper function to get location - uses different field priority based on case type
  const getLocationForNarrative = () => {
    if (isUnidentified || isUnclaimed) {
      // For remains, prioritize recovery/found location
      return caseData.recovery_location || 
             caseData.found_location || 
             caseData.last_seen_location || 
             [caseData.city, caseData.state].filter(Boolean).join(', ');
    }
    // For missing persons, use last seen location
    return caseData.last_seen_location || 
           [caseData.city, caseData.state].filter(Boolean).join(', ');
  };
  
  if (isUnidentified) {
    // Unidentified person overview
    const dateFound = formatDisplayDate(caseData.date_found || caseData.date_body_found);
    const location = getLocationForNarrative();
    
    if (dateFound && location) {
      parts.push(`Unidentified remains were discovered on ${dateFound} in ${location}.`);
    } else if (location) {
      parts.push(`Unidentified remains were discovered in ${location}.`);
    } else if (dateFound) {
      parts.push(`Unidentified remains were discovered on ${dateFound}.`);
    }
    
    // Demographics
    const demo = [];
    if (caseData.gender) demo.push(caseData.gender.toLowerCase());
    if (caseData.age) demo.push(`estimated age ${caseData.age}`);
    else if (caseData.age_range) demo.push(`estimated age ${caseData.age_range}`);
    if (caseData.race) demo.push(caseData.race);
    
    if (demo.length > 0) {
      parts.push(`The individual is described as a ${demo.join(', ')}.`);
    }
    
    // Physical description
    const physical = [];
    if (caseData.hair_color) physical.push(`${caseData.hair_color.toLowerCase()} hair`);
    if (caseData.eye_color) physical.push(`${caseData.eye_color.toLowerCase()} eyes`);
    if (caseData.height || caseData.height_display) physical.push(`approximately ${caseData.height || caseData.height_display} tall`);
    if (caseData.weight || caseData.weight_lbs) physical.push(`weighing ${caseData.weight || caseData.weight_lbs + ' lbs'}`);
    
    if (physical.length > 0) {
      parts.push(`Physical characteristics include ${physical.join(', ')}.`);
    }
    
  } else if (isUnclaimed) {
    // Unclaimed person overview
    const dateFound = formatDisplayDate(caseData.date_body_found || caseData.date_of_death);
    const location = getLocationForNarrative();
    
    if (dateFound && location) {
      parts.push(`This unclaimed person was found on ${dateFound} in ${location}.`);
    }
    
    if (caseData.age) {
      parts.push(`The individual was ${caseData.age} years old at the time of death.`);
    }
    
  } else {
    // Missing person overview
    const lastSeenDate = formatDisplayDate(caseData.last_seen_date || caseData.missing_since);
    const location = getLocationForNarrative();
    
    if (lastSeenDate && location) {
      parts.push(`${name} was last seen on ${lastSeenDate} in ${location}.`);
    } else if (lastSeenDate) {
      parts.push(`${name} was last seen on ${lastSeenDate}.`);
    } else if (location) {
      parts.push(`${name} was last seen in ${location}.`);
    }
    
    // Demographics
    const demo = [];
    if (caseData.age) demo.push(`${caseData.age} years old`);
    if (caseData.gender) demo.push(caseData.gender.toLowerCase());
    if (caseData.race) demo.push(caseData.race);
    
    if (demo.length > 0) {
      parts.push(`At the time of disappearance, ${name.split(' ')[0]} was ${demo.join(', ')}.`);
    }
    
    // Physical description
    const physical = [];
    if (caseData.hair_color) physical.push(`${caseData.hair_color.toLowerCase()} hair`);
    if (caseData.eye_color) physical.push(`${caseData.eye_color.toLowerCase()} eyes`);
    if (caseData.height || caseData.height_display) physical.push(`approximately ${caseData.height || caseData.height_display} tall`);
    if (caseData.weight || caseData.weight_lbs) physical.push(`weighing ${caseData.weight || caseData.weight_lbs + ' lbs'}`);
    
    if (physical.length > 0) {
      parts.push(`Physical description: ${physical.join(', ')}.`);
    }
  }
  
  return parts.join(' ');
};

// Extract first meaningful paragraph as official summary (up to ~3 sentences)
const extractOfficialSummary = (description, circumstances) => {
  const text = circumstances || description;
  if (!text) return null;
  
  // Split by sentence endings
  const sentences = text.split(/(?<=[.!?])\s+/);
  
  // Take first 3 sentences max, or up to 500 chars
  let summary = '';
  let count = 0;
  for (const sentence of sentences) {
    if (count >= 3 || summary.length + sentence.length > 500) break;
    summary += (summary ? ' ' : '') + sentence.trim();
    count++;
  }
  
  return summary || null;
};

// Check if narrative is from a rich source like Charley Project
const hasExpandedNarrative = (caseData) => {
  if (caseData.source === 'charley') return true;
  
  // Check if description is long enough to be a narrative (more than 500 chars)
  if (caseData.description && caseData.description.length > 500) return true;
  
  return false;
};

// Get source info for transparency section
const getSourceInfo = (caseData) => {
  const sources = [];
  
  // If merged/canonical case, show the actual sources it was merged from
  if ((caseData.source === 'canonical' || caseData.is_canonical) && caseData.sources && caseData.sources.length > 0) {
    caseData.sources.forEach((src, idx) => {
      const srcConfig = SOURCE_CONFIG[src] || { 
        name: src?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown', 
        fullName: src,
        color: 'bg-slate-100 text-slate-800 dark:bg-slate-900/30 dark:text-slate-400'
      };
      sources.push({
        ...srcConfig,
        isPrimary: idx === 0,
        url: idx === 0 ? caseData.source_url : null
      });
    });
  } else {
    // Non-canonical case - show the primary source
    const primarySource = SOURCE_CONFIG[caseData.source] || {
      name: caseData.source_name || caseData.source?.toUpperCase() || 'Unknown',
      fullName: caseData.source_name || 'Unknown Source',
      color: 'bg-slate-100 text-slate-800 dark:bg-slate-900/30 dark:text-slate-400'
    };
    
    sources.push({
      ...primarySource,
      url: caseData.source_url,
      isPrimary: true
    });
  }
  
  // Add investigating agency if available
  if (caseData.investigating_agency) {
    const agency = typeof caseData.investigating_agency === 'string' 
      ? caseData.investigating_agency 
      : caseData.investigating_agency.name || caseData.investigating_agency.agencyName;
    if (agency) {
      sources.push({
        name: 'Law Enforcement',
        fullName: agency,
        color: 'bg-slate-100 text-slate-800 dark:bg-slate-900/30 dark:text-slate-400',
        isPrimary: false
      });
    }
  }
  
  return sources;
};

export default function CaseDescriptionSection({ caseData }) {
  const [narrativeExpanded, setNarrativeExpanded] = useState(false);
  
  if (!caseData) return null;
  
  const primaryOverview = buildPrimaryOverview(caseData);
  const officialSummary = extractOfficialSummary(caseData.description, caseData.circumstances);
  const hasNarrative = hasExpandedNarrative(caseData);
  const sources = getSourceInfo(caseData);
  
  // For Charley Project cases, the entire description is the narrative
  const isCharleyCase = caseData.source === 'charley';
  
  // Determine if we should show the narrative section
  // Show it if we have rich narrative content that differs from the summary
  const showNarrativeSection = hasNarrative && caseData.description && 
    (!officialSummary || caseData.description !== officialSummary);
  
  return (
    <Card data-testid="case-description-section">
      <CardHeader>
        <CardTitle className="font-heading flex items-center gap-2">
          <FileText className="w-5 h-5 text-trust-blue-500" />
          Case Information
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        
        {/* 1. Primary Case Overview */}
        {primaryOverview && (
          <div className="space-y-2" data-testid="primary-overview">
            <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-2">
              <User className="w-4 h-4" />
              Overview
            </h4>
            <p className="text-foreground leading-relaxed">
              {primaryOverview}
            </p>
          </div>
        )}
        
        {/* 2. Official Circumstances Summary */}
        {officialSummary && !isCharleyCase && (
          <div className="bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg p-4" data-testid="official-circumstances">
            <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-2">
              <Shield className="w-4 h-4" />
              Circumstances (Official Record)
            </h4>
            <p className="text-foreground leading-relaxed">
              {officialSummary}
            </p>
          </div>
        )}
        
        {/* 3. Expanded Narrative Section - Collapsible on mobile */}
        {showNarrativeSection && (
          <div className="border border-purple-200 dark:border-purple-800 rounded-lg overflow-hidden" data-testid="expanded-narrative">
            <Collapsible open={narrativeExpanded} onOpenChange={setNarrativeExpanded}>
              <CollapsibleTrigger asChild>
                <button className="w-full bg-purple-50 dark:bg-purple-900/20 px-4 py-3 flex items-center justify-between hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors">
                  <div className="flex items-center gap-2">
                    <BookOpen className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                    <span className="text-sm font-semibold uppercase tracking-wide text-purple-700 dark:text-purple-400">
                      Expanded Narrative
                      {isCharleyCase && (
                        <Badge variant="outline" className="ml-2 text-xs bg-purple-100 text-purple-700 border-purple-300 dark:bg-purple-900/50 dark:text-purple-400">
                          The Charley Project
                        </Badge>
                      )}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-purple-600 dark:text-purple-400">
                    <span className="text-xs hidden sm:inline">
                      {narrativeExpanded ? 'Collapse' : 'Expand'}
                    </span>
                    {narrativeExpanded ? (
                      <ChevronUp className="w-5 h-5" />
                    ) : (
                      <ChevronDown className="w-5 h-5" />
                    )}
                  </div>
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="p-4 bg-white dark:bg-slate-900">
                  <p className="text-muted-foreground leading-relaxed whitespace-pre-line">
                    {caseData.description}
                  </p>
                  {isCharleyCase && caseData.source_url && (
                    <div className="mt-4 pt-3 border-t border-purple-200 dark:border-purple-800">
                      <a 
                        href={caseData.source_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-sm text-purple-600 hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-300"
                      >
                        <ExternalLink className="w-4 h-4" />
                        Read full case profile on The Charley Project
                      </a>
                    </div>
                  )}
                </div>
              </CollapsibleContent>
            </Collapsible>
            
            {/* Show preview on mobile when collapsed */}
            {!narrativeExpanded && (
              <div className="px-4 pb-3 pt-0 md:hidden">
                <p className="text-sm text-muted-foreground line-clamp-3">
                  {caseData.description?.slice(0, 200)}...
                </p>
              </div>
            )}
          </div>
        )}
        
        {/* Simple description fallback for cases without rich narrative */}
        {!showNarrativeSection && caseData.description && !officialSummary && (
          <div data-testid="simple-description">
            <p className="text-muted-foreground leading-relaxed">
              {caseData.description}
            </p>
          </div>
        )}
        
        {/* 4. Source Transparency Section */}
        <div className="border-t border-slate-200 dark:border-slate-700 pt-4" data-testid="source-transparency">
          <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3 flex items-center gap-2">
            <Database className="w-4 h-4" />
            Data Sources
          </h4>
          <div className="flex flex-wrap gap-2">
            {sources.map((source, idx) => (
              <div key={idx} className="flex items-center gap-2">
                {source.url ? (
                  <a
                    href={source.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors hover:opacity-80 ${source.color}`}
                  >
                    {source.name}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                ) : (
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${source.color}`}>
                    {source.name}
                  </span>
                )}
                {source.isPrimary && sources.length > 1 && (
                  <span className="text-xs text-muted-foreground">(Primary)</span>
                )}
              </div>
            ))}
          </div>
          
          {/* Additional source info if available */}
          {caseData.namus_number && (
            <p className="text-xs text-muted-foreground mt-2">
              NamUs Case Number: {caseData.namus_number}
            </p>
          )}
          {caseData.case_number && (
            <p className="text-xs text-muted-foreground mt-1">
              Agency Case Number: {caseData.case_number}
            </p>
          )}
        </div>
        
      </CardContent>
    </Card>
  );
}
